// Single source of truth for "what kind of prompt block is this, and which screen(s) does it
// belong to" — shared by the Instructions tab and the Modules tab, so the two views can never
// classify the same agent's system_prompts differently (mirrors toolPlacement.ts's role for tools).
//
// Three-level model (see api-service/app/services/llm/prompt_generation.py and TOOL_SCOPE.md):
//   global   — no home_module, no topic_modules, no condition_modules. Always included, every
//              turn. Edited from the Instructions tab only.
//   general  — home_module set. Organizationally tied to one screen (edited from that screen's
//              card in the Modules tab), but functionally UNCONDITIONAL — included every turn
//              regardless of the active screen, exactly like a global block. home_module always
//              wins: a general block is never gated even if it also carries a stray
//              topic_modules/condition_modules value.
//   specific — no home_module, condition_modules empty, topic_modules is exactly one module.
//              Only included when that module is the active screen. No title constraint — unlike
//              an earlier draft of this model, "General"/"Specific" are section labels in the UI,
//              not required block titles.
//   legacy   — anything else scoped (condition_modules non-empty, or topic_modules spanning more
//              than one module), without home_module. Still gates correctly at runtime (backward
//              compatible) but doesn't fit the new convention — surfaced with a "Convert" action
//              rather than silently migrated.

// Mirrors api-service/app/services/llm/module_aliases.py's MODULE_MERGE_FALLBACKS — Workflow +
// Properties + Quick Quote resolve as one module. Applied here so a legacy block still tagged
// "properties"/"quick_quote" surfaces under the merged Workflow & Properties card instead of an
// orphaned slug that no longer has its own card.
const MODULE_MERGE_FALLBACKS: Record<string, string> = { properties: 'workflow', quick_quote: 'workflow' };
export const canonicalModule = (m: string): string => MODULE_MERGE_FALLBACKS[m] ?? m;

export type PromptKind = 'global' | 'general' | 'specific' | 'card' | 'legacy';

export interface PromptPlacement {
  kind: PromptKind;
  /** [] for global and card; [module] for general/specific; 1+ for legacy. */
  modules: string[];
  /** card only: the card ids / "card#sub-card" keys the block is scoped to. */
  cards?: string[];
}

export interface PromptBlockLike {
  title?: string;
  topic_modules?: string[];
  topic_cards?: string[];
  condition_modules?: string[];
  home_module?: string;
}

//   card     — no home_module, condition_modules empty, topic_cards non-empty. Only included while one of
//              those cards / sections is the active one (the same intersection gate as `specific`, one level
//              down — api-service cards/scope.py). Edited from the owning module's "Cards & sections".

export function classifyPrompt(block: PromptBlockLike): PromptPlacement {
  const home = block.home_module?.trim();
  if (home) return { kind: 'general', modules: [home] };

  const cards = (block.topic_cards ?? []).filter(Boolean);
  if (cards.length > 0 && (block.condition_modules ?? []).length === 0) return { kind: 'card', modules: [], cards };

  const topics = block.topic_modules ?? [];
  const conditions = block.condition_modules ?? [];
  if (conditions.length === 0 && topics.length === 0) return { kind: 'global', modules: [] };
  if (conditions.length === 0) return { kind: 'specific', modules: [...topics] };

  const modules = [...new Set([...topics, ...conditions])];
  return { kind: 'legacy', modules };
}

export function classifyPrompts(blocks: PromptBlockLike[]): Map<number, PromptPlacement> {
  const result = new Map<number, PromptPlacement>();
  blocks.forEach((b, i) => result.set(i, classifyPrompt(b)));
  return result;
}

const indicesOfKind = (placements: Map<number, PromptPlacement>, kind: PromptKind): number[] =>
  [...placements].filter(([, p]) => p.kind === kind).map(([i]) => i);

export const globalBlockIndices = (placements: Map<number, PromptPlacement>): number[] =>
  indicesOfKind(placements, 'global');

export const generalBlocksFor = (placements: Map<number, PromptPlacement>, module: string): number[] =>
  [...placements].filter(([, p]) => p.kind === 'general' && canonicalModule(p.modules[0]) === module).map(([i]) => i);

/** Specific blocks scoped to this module, including one shared with other screens — the backend
 *  gates on a set intersection, so a shared block belongs under every screen it names. */
export const specificBlocksFor = (placements: Map<number, PromptPlacement>, module: string): number[] =>
  [...placements]
    .filter(([, p]) => p.kind === 'specific' && p.modules.some((m) => canonicalModule(m) === module))
    .map(([i]) => i);

/** Card-scoped blocks naming exactly this card id or "card#sub-card" key. */
export const cardBlocksFor = (placements: Map<number, PromptPlacement>, key: string): number[] =>
  [...placements].filter(([, p]) => p.kind === 'card' && (p.cards ?? []).includes(key)).map(([i]) => i);

export const legacyBlocksFor =(placements: Map<number, PromptPlacement>, module: string): number[] =>
  [...placements].filter(([, p]) => p.kind === 'legacy' && p.modules.some((m) => canonicalModule(m) === module)).map(([i]) => i);

/** The (from, to) real-array indices to swap when moving the element at position `pos` within a
 * filtered subset of indices by `dir` — shared by any tab that reorders a filtered view of
 * `system_prompts` (Instructions' global list, a screen's General/Specific lists) without
 * disturbing the position of blocks outside that subset. Returns null at the subset's edge. */
export function moveWithinSubset(subset: number[], pos: number, dir: -1 | 1): [number, number] | null {
  const newPos = pos + dir;
  if (newPos < 0 || newPos >= subset.length) return null;
  return [subset[pos], subset[newPos]];
}
